import dayjs from 'dayjs';
import 'dayjs/locale/sq';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(localizedFormat);
dayjs.extend(relativeTime);
dayjs.locale('sq');

export { dayjs };

/**
 * Full month names, lower-case as Albanian writes them ("20 shtator 2026"). Short forms like
 * "Sht" are ambiguous (shtator / shkurt) and harder to read.
 */
const fmt = (d: string | Date | dayjs.Dayjs, pattern: string) =>
  dayjs(d).format(pattern).toLowerCase();

export const fmtDate = (d: string | Date) => fmt(d, 'D MMMM YYYY');
export const fmtDateTime = (d: string | Date) => fmt(d, 'D MMMM YYYY, HH:mm');
export const fmtTime = (d: string | Date) => fmt(d, 'HH:mm');

export function fmtRange(start: string | Date, end: string | Date) {
  const s = dayjs(start);
  const e = dayjs(end);
  return s.isSame(e, 'day')
    ? `${fmt(s, 'D MMMM YYYY')}, ${fmt(s, 'HH:mm')} - ${fmt(e, 'HH:mm')}`
    : `${fmt(s, 'D MMMM YYYY, HH:mm')} - ${fmt(e, 'D MMMM YYYY, HH:mm')}`;
}

/**
 * When something happened, the way people say it: "tani", "para 5 minutash", "para 2 orësh",
 * "dje, 14:30", then the date ("12 shtator, 14:30", with the year only if it is not this year).
 */
export function fmtAgo(d: string | Date, now: Date = new Date()) {
  const then = dayjs(d);
  const minutes = Math.floor(dayjs(now).diff(then, 'minute'));
  if (minutes < 1) return 'tani';
  if (minutes < 60) return minutes === 1 ? 'para 1 minute' : `para ${minutes} minutash`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24 && then.isSame(now, 'day')) {
    return hours === 1 ? 'para 1 ore' : `para ${hours} orësh`;
  }
  if (then.isSame(dayjs(now).subtract(1, 'day'), 'day')) return `dje, ${fmt(then, 'HH:mm')}`;
  return then.isSame(now, 'year') ? fmt(then, 'D MMMM, HH:mm') : fmt(then, 'D MMMM YYYY, HH:mm');
}

interface CalendarDateParts {
  year: number;
  month: number; // 0-based, as FullCalendar provides it
  day: number;
}

/**
 * Natural Albanian titles for the calendar header (the browser's built-in Albanian date
 * data is incomplete and produces labels like "2026 M09"). FullCalendar passes an
 * inclusive end date here.
 */
export function calendarTitle(start: CalendarDateParts, end?: CalendarDateParts | null) {
  const s = dayjs(new Date(start.year, start.month, start.day));
  const e = end ? dayjs(new Date(end.year, end.month, end.day)) : s;
  const cap = (x: string) => x.charAt(0).toUpperCase() + x.slice(1);
  if (s.isSame(e, 'day')) return cap(fmt(s, 'dddd, D MMMM YYYY'));
  if (s.date() === 1 && e.isSame(s.endOf('month'), 'day')) return cap(fmt(s, 'MMMM YYYY'));
  if (s.isSame(e, 'month')) return `${s.date()} - ${fmt(e, 'D MMMM YYYY')}`;
  if (s.isSame(e, 'year')) return `${fmt(s, 'D MMMM')} - ${fmt(e, 'D MMMM YYYY')}`;
  return `${fmt(s, 'D MMMM YYYY')} - ${fmt(e, 'D MMMM YYYY')}`;
}

/** Weekday header for the calendar grid: "Hën", or "Hën 14" in the week view. */
export function calendarDayHeader(date: CalendarDateParts, withDay = false) {
  const d = dayjs(new Date(date.year, date.month, date.day));
  const name = d.format('ddd');
  return withDay ? `${name} ${d.date()}` : name;
}

interface CalendarTimeParts extends CalendarDateParts {
  hour: number;
  minute: number;
}

const hhmm = (p: CalendarTimeParts) =>
  `${String(p.hour).padStart(2, '0')}:${String(p.minute).padStart(2, '0')}`;

/** Event times in the calendar: "10:00" or "10:00 - 12:00" (plain hyphen, 24-hour clock). */
export function calendarEventTime(start: CalendarTimeParts, end?: CalendarTimeParts | null) {
  return end && hhmm(end) !== hhmm(start) ? `${hhmm(start)} - ${hhmm(end)}` : hhmm(start);
}
